import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  IconButton,
  Slider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DrawIcon from "@mui/icons-material/Draw";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import GuestRegistrationForm from "./GuestRegistrationForm";
import { useDispatch, useSelector } from "react-redux";
import { saveGuestRegistrationData } from "../../redux/slice/slice";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../utils/constants";

// initialState for the guest registration form
const initialState = {
  registrationNo: "",
  paymentMode: "",

  guestName: "",
  nationality: "",
  email: "",
  phone: "",

  source: "",
  bookingAmount: "",

  address: "",
  visaNumber: "",
  visaIssueDate: "",
  visaExpiryDate: "",

  dob: "",
  purposeOfVisit: "",
  arrivedFrom: "",
  preferences: "",

  companyName: "",
  companyGST: "",
  designation: "",
  companyAddress: "",
  companyEmail: "",
  companyContact: "",

  vehicleNo: "",
  anniversary: "",
  remark: "",

  consent: false,
  signatureDataUrl: null,
};

const resetInitialState = {
  registrationNo: "",
  // paymentMode: "",

  // guestName: "",
  // nationality: "",
  // email: "",
  // phone: "",

  // source: "",
  // bookingAmount: "",

  address: "",
  visaNumber: "",
  visaIssueDate: "",
  visaExpiryDate: "",

  dob: "",
  purposeOfVisit: "",
  arrivedFrom: "",
  preferences: "",

  companyName: "",
  companyGST: "",
  designation: "",
  companyAddress: "",
  companyEmail: "",
  companyContact: "",

  vehicleNo: "",
  anniversary: "",
  remark: "",

  consent: false,
  signatureDataUrl: null,
};

// ---------- Utilities ----------
function drawImageCover(ctx, img, x, y, w, h) {
  // draw image to fill area while preserving aspect ratio (like CSS background-size: cover)
  const iw = img.width;
  const ih = img.height;
  const scale = Math.max(w / iw, h / ih);
  const nw = iw * scale;
  const nh = ih * scale;
  const nx = x + (w - nw) / 2;
  const ny = y + (h - nh) / 2;
  ctx.drawImage(img, nx, ny, nw, nh);
}

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = reject;
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(file);
  });
}

function useHiDPICanvas(canvasRef, { bg = "#fff", fixedHeight = 220 } = {}) {
  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement || canvas;
    const rect = parent.getBoundingClientRect();
    const dpr = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.max(320, Math.floor(rect.width || 640));
    const height = fixedHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, width, height);
  };

  useEffect(() => {
    resize();
    const ro = new ResizeObserver(() => resize());
    const node = canvasRef.current?.parentElement;
    if (node) ro.observe(node);
    return () => ro.disconnect();
  }, []);
}

// ---------- Draw Mode ----------
function SignatureCanvas({
  penColor,
  penSize,
  onChange,
  background = "#fff",
  isClearSignature,
  setIsClearSignature,
}) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  useHiDPICanvas(canvasRef, { bg: background });

  const getCtx = () => canvasRef.current.getContext("2d");

  const getLocalXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    let x, y;
    if (e.touches && e.touches[0]) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }
    return { x, y };
  };

  const begin = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getLocalXY(e);
    const ctx = getCtx();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getLocalXY(e);
    const ctx = getCtx();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = penColor;
    ctx.lineWidth = penSize;
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = getCtx();
    const w = parseInt(c.style.width, 10) || c.width;
    const h = parseInt(c.style.height, 10) || c.height;
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, w, h);
    onChange?.(null);
  };

  useEffect(() => {
    if (isClearSignature) {
      clear();
      setIsClearSignature(false);
    }
  }, [isClearSignature]);

  return (
    <Box>
      <canvas
        ref={canvasRef}
        onMouseDown={begin}
        onMouseMove={draw}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={begin}
        onTouchMove={draw}
        onTouchEnd={end}
        style={{
          width: "100%",
          height: 220,
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          background,
          touchAction: "none",
          display: "block",
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Tooltip title="Clear" sx={{ color: "#fff" }}>
          <IconButton aria-label="clear" onClick={clear}>
            <RestartAltIcon sx={{ color: "#fff" }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}

// ---------- Upload Mode ----------
function UploadSignature({ onChange, targetWidth = 640, targetHeight = 220 }) {
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const normalize = useCallback(
    async (file) => {
      try {
        setError(null);
        const url = await fileToDataURL(file);
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, targetWidth, targetHeight);
          drawImageCover(ctx, img, 0, 0, targetWidth, targetHeight);
          const out = canvas.toDataURL("image/png");
          onChange?.(out);
        };
        img.onerror = () => setError("Could not load image.");
        img.src = url;
      } catch (e) {
        setError(e.message || "Upload failed.");
      }
    },
    [onChange, targetWidth, targetHeight]
  );

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) normalize(file);
        }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => inputRef.current?.click()}
        >
          Choose image
        </Button>
        <Typography variant="body2" color="text.secondary">
          PNG/JPG preferred. We'll fit it into a standard signature box.
        </Typography>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

// ---------- Camera Mode ----------
function CameraSignature({ onChange, targetWidth = 640, targetHeight = 220 }) {
  const [error, setError] = useState(null);
  const [captured, setCaptured] = useState(null);
  const fileInputRef = useRef(null); // opens native camera

  const onNativePicked = async (e) => {
    try {
      setError(null);
      const file = e.target.files?.[0];
      if (!file) return;

      const url = await fileToDataURL(file);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, targetWidth, targetHeight);
        drawImageCover(ctx, img, 0, 0, targetWidth, targetHeight);
        const out = canvas.toDataURL("image/png");
        setCaptured(out);
        onChange?.(out);
      };
      img.onerror = () => setError("Could not load captured image.");
      img.src = url;
    } catch {
      setError("Camera/photo not accessible.");
    }
  };

  const retake = () => {
    setCaptured(null);
    onChange?.(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <Stack spacing={1}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems="center"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment" // <-- photo mode
            style={{ display: "none" }}
            onChange={onNativePicked}
          />
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Take photo
          </Button>
          {captured && (
            <Button variant="text" onClick={retake}>
              Retake
            </Button>
          )}
        </Stack>

        {!captured && (
          <Typography variant="body2" color="text.secondary">
            Tap <strong>Take photo</strong> to capture your signature.
          </Typography>
        )}
        {error && <Alert severity="warning">{error}</Alert>}

        {captured && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Preview
            </Typography>
            <Box
              component="img"
              src={captured}
              alt="captured"
              sx={{
                width: 320,
                border: "1px dashed #e5e7eb",
                borderRadius: 1.5,
              }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ---------- Main Component ----------
export default function DigitalSignature() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const HotelData = useSelector((state) => state?.HotelData);
  const HomepageData = useSelector((state) => state?.HomepageData);
  const guestRegistrationData = useSelector((state) => state?.guestRegistrationData);
  const hotelId = useSelector((state) => state?.HotelId);
  const [penColor, setPenColor] = useState("#111111");
  const [penSize, setPenSize] = useState(3);
  const [formData, setFormData] = useState(initialState);
  const [consent, setConsent] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("draw"); // 'draw' | 'upload' | 'camera'
  const [isClearSignature, setIsClearSignature] = useState(false);
  const [registrationData, setRegistrationData] = useState({});
  const[registeredGuestData, setRegisteredGuestData] = useState({});

  const handleData = (url) => {
    setDataUrl(url);
  };

  const handleCopy = async () => {
    if (!dataUrl) return;
    try {
      await navigator.clipboard.writeText(dataUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // ignore
    }
  };

  const handleDownload = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `signature-${Date.now()}.png`;
    a.click();
  };

  const Actions = (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Download PNG">
        <span>
          <IconButton onClick={handleDownload} disabled={!dataUrl || !consent}>
            <FileDownloadIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={copied ? "Copied!" : "Copy data URL"}>
        <span>
          <IconButton onClick={handleCopy} disabled={!dataUrl || !consent}>
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Box flex={1} />
      <Button
        variant="contained"
        disabled={!dataUrl || !consent}
        onClick={() => alert("Submit signature to your API here.")}
      >
        Use this signature
      </Button>
    </Stack>
  );

  const handleSubmit = () => {
    if (!dataUrl) {
      alert("Please provide a signature.");
      return;
    }
    if (!consent) {
      alert("Please provide consent.");
      return;
    }
    // Submit the form data and signature dataUrl to your API here
    console.log("Form Data:", formData);
    console.log("Signature Data URL:", dataUrl);
    dispatch(
      saveGuestRegistrationData({
        ...formData,
        signatureDataUrl: dataUrl,
        consent,
      })
    );
    navigate("/reg-preview", {
      state: {
        regData: { ...formData, signatureDataUrl: dataUrl, consent },
        HotelData: HotelData,
        HomepageData: HomepageData,
      },
    });
  };

  const getRegistrationData = () => {
    axios
      .get(
        `${baseUrl}/web-api/get-registration-card-detail?hotelId=${hotelId}&bookingId=${HomepageData?.booking?.bookingId}`
      )
      .then((res) => {
        setRegistrationData(res?.data?.message);
        console.log("registeredGuestData",JSON.parse(res?.data?.message?.guestSignatureInfo?.guest_detail));
        const regData= res?.data?.message?.guestSignatureInfo?.guest_detail ? JSON.parse(res?.data?.message?.guestSignatureInfo?.guest_detail) : {};
        setRegisteredGuestData(regData);
      })
      .catch(() => {});
  };
  useEffect(() => {
    getRegistrationData();
  }, []);

  useEffect(() => {
    const regData = {
      registrationNo:registeredGuestData?.registration_no || guestRegistrationData?.registrationNo || registeredGuestData?.registrationNo || "",  
      paymentMode: registeredGuestData?.payment_mode || registrationData?.paymentMode || guestRegistrationData?.paymentMode || "",

      guestName:registeredGuestData?.guest_name || registrationData?.guestName || guestRegistrationData?.guestName || "",
      nationality:registeredGuestData?.nationality || registrationData?.nationality || "",
        email:registeredGuestData?.email || registrationData?.email || guestRegistrationData?.email || "",
      phone:registeredGuestData?.phone || registrationData?.phone || guestRegistrationData?.phone || "",

      source:registeredGuestData?.source || registrationData?.source || guestRegistrationData?.source || "",
      bookingAmount:registeredGuestData?.booking_amount || registrationData?.bookingAmount || guestRegistrationData?.bookingAmount || "",

      address:registeredGuestData?.address || registrationData?.address || guestRegistrationData?.address || "",
      visaNumber:registeredGuestData?.visa_number || registrationData?.visaNumber || guestRegistrationData?.visaNumber || "",
      visaIssueDate:registeredGuestData?.visa_issue_date || registrationData?.visaIssueDate || guestRegistrationData?.visaIssueDate || "",
      visaExpiryDate:registeredGuestData?.visa_expiry_date || registrationData?.visaExpiryDate || guestRegistrationData?.visaExpiryDate || "",

      dob:registeredGuestData?.dob || registrationData?.dob || guestRegistrationData?.dob || "",
      purposeOfVisit:registeredGuestData?.purpose_of_visit || registrationData?.purposeOfVisit || guestRegistrationData?.purposeOfVisit || "",
      arrivedFrom:registeredGuestData?.arrived_from || registrationData?.arrivedFrom || guestRegistrationData?.arrivedFrom || "",
      preferences:registeredGuestData?.preferences || registrationData?.preferences || guestRegistrationData?.preferences || "",

      companyName:registeredGuestData?.company_name || registrationData?.companyName || guestRegistrationData?.companyName || "",
      companyGST:registeredGuestData?.company_gst || registrationData?.companyGST || guestRegistrationData?.companyGST || "",
      designation:registeredGuestData?.designation || registrationData?.designation || guestRegistrationData?.designation || "",
      companyAddress:registeredGuestData?.company_address || registrationData?.companyAddress || guestRegistrationData?.companyAddress || "",
      companyEmail:registeredGuestData?.company_email || registrationData?.companyEmail || guestRegistrationData?.companyEmail || "",
      companyContact:registeredGuestData?.company_contact || registrationData?.companyContact || guestRegistrationData?.companyContact || "",

      vehicleNo:registeredGuestData?.vehicle_no || registrationData?.vehicleNo || guestRegistrationData?.vehicleNo || "",
      anniversary:registeredGuestData?.anniversary || registrationData?.anniversary || guestRegistrationData?.anniversary || "",
      remark:registeredGuestData?.remark || registrationData?.remark || guestRegistrationData?.remark || "",

      consent:registeredGuestData?.consent || registrationData?.consent || guestRegistrationData?.consent || false,
      signatureDataUrl: null,
    };
    setFormData(regData);
  }, [registrationData]);
  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <GuestRegistrationForm formData={formData} setFormData={setFormData} />
      <Card
        elevation={0}
        sx={{
          width: "100%",
          margin: "auto",
          flexGrow: 1,
          backgroundColor: "rgba(30, 30, 30, 0.05)",
          zIndex: 1,
          mt: 3,
          borderRadius: 2,
        }}
      >
        <CardHeader
          sx={{ p: 0 }}
          title={
            <Typography variant="subtitle1" gutterBottom>
              Signature
            </Typography>
          }
        />
        <CardContent sx={{ p: 0 }}>
          <Stack spacing={4}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                handleData(null);
              }}
              aria-label="signature mode"
              variant="fullWidth"
            >
              <Tab
                icon={<DrawIcon />}
                iconPosition="start"
                label="Draw"
                value="draw"
              />
              <Tab
                icon={<UploadFileIcon />}
                iconPosition="start"
                label="Upload"
                value="upload"
              />
              {/* <Tab
                icon={<PhotoCameraIcon />}
                iconPosition="start"
                label="Camera"
                value="camera"
              /> */}
            </Tabs>

            {/* Mode content */}
            {tab === "draw" && (
              <SignatureCanvas
                penColor={penColor}
                penSize={penSize}
                onChange={handleData}
                isClearSignature={isClearSignature}
                setIsClearSignature={setIsClearSignature}
              />
            )}
            {tab === "upload" && <UploadSignature onChange={handleData} />}
            {tab === "camera" && <CameraSignature onChange={handleData} />}

            {dataUrl && (
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview
                </Typography>
                <Box
                  component="img"
                  src={dataUrl}
                  alt="signature preview"
                  sx={{
                    width: 320,
                    height: "auto",
                    border: "1px dashed #e5e7eb",
                    borderRadius: 1.5,
                    background: "#fff",
                  }}
                />
              </Stack>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
              }
              label="I agree this signature represents my intent to sign electronically."
            />
          </Stack>
        </CardContent>
      </Card>
      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button variant="light-button" onClick={handleSubmit}>
          Preview
        </Button>
        <Button
          variant="dark-button"
          onClick={() => {
            handleData(null);
            setIsClearSignature(true);
            setFormData({...resetInitialState, paymentMode: formData?.paymentMode || "",
              guestName: formData?.guestName || "",
              nationality: formData?.nationality || "",
              email: formData?.email || "",
              phone: formData?.phone || "",
              source: formData?.source || "",
              bookingAmount: formData?.bookingAmount || "",});
            setConsent(formData?.consent || false);
          }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
}
